/**
 * Social Login Boot File
 *
 * Initializes @capgo/capacitor-social-login on native platforms so that
 * useSocialLogin's native Google branch can call SocialLogin.login() without
 * a per-call init handshake.
 *
 * Web is a no-op — the web SSO path uses the redirect/Browser flow handled
 * elsewhere.
 *
 * Why a boot file (vs lazy init in the composable): the plugin's initialize()
 * is a one-shot per-process operation. Doing it once at app start avoids race
 * conditions where the user taps Sign-in-with-Google before the plugin is
 * ready, and lets us surface a clear error in dev logs if VITE_GOOGLE_WEB_CLIENT_ID
 * is missing for the current tier (would otherwise fail silently mid-login).
 */

import { defineBoot } from '#q-app';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { platform, devLog, devError } from '@shared/vue/utils/debug';
import { isProviderEnabled } from 'src/config/socialProviders';

export default defineBoot(async () => {
  if (!platform.isNativeApp()) {
    return;
  }

  // Visibility toggle is the gate before everything else. If the env has
  // disabled a provider, we don't pull credentials from it — even if they
  // happen to be set — because the button isn't in the UI either. Keeps
  // boot output and plugin state honest about what's actually enabled.
  const googleEnabled = isProviderEnabled('google');
  const appleEnabled = isProviderEnabled('apple');

  // VITE_GOOGLE_WEB_CLIENT_ID is the Google Cloud Console OAuth 2.0 "Web
  // application" client ID. Its purpose here is twofold:
  //   1. The plugin uses it as the audience the ID token will be issued for
  //      (Google issues the ID token with `aud = webClientId`, even when the
  //      caller is an Android app — that's the "serverClientId" pattern).
  //   2. The API verifies that audience match in V1SocialiteController::nativeCallback,
  //      so the value MUST match the GOOGLE_ALLOWED_AUDIENCES allowlist on the API.
  // See the runbook at wiki/runbooks/<date>_google_signin_setup.md.
  const webClientId = googleEnabled ? import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID : undefined;

  // Apple Sign-In wiring (iOS-native + Android-WebView fallback). Optional
  // per tier — if VITE_APPLE_CLIENT_ID isn't set we skip the apple block
  // and Apple sign-in falls back to the redirect/Custom-Tab path.
  //
  // The Apple `clientId` value is the **Apple Service ID** (e.g.
  // com.emalino.signin), NOT the iOS bundle id. Apple's iOS native flow
  // uses the bundle id as the audience automatically; the plugin's
  // configured clientId here is what gets used for the Android-WebView
  // and web flows. Both bundle ids and the Service ID need to be in
  // APPLE_ALLOWED_AUDIENCES on the API side.
  const appleClientId = appleEnabled ? import.meta.env.VITE_APPLE_CLIENT_ID : undefined;

  // No-op if neither provider is configured (operator chose to ship without
  // native SSO for this tier).
  if (!webClientId && !appleClientId) {
    devLog(
      '[SocialLogin] No native provider enabled for this tier ' +
        `(google_enabled=${googleEnabled}, apple_enabled=${appleEnabled}). ` +
        'Redirect path still works on web/iOS.',
    );
    return;
  }

  // Only complain about missing client IDs for providers the env says are
  // enabled — otherwise an operator who disables Google in env shouldn't
  // see a "VITE_GOOGLE_WEB_CLIENT_ID is not set" warning every boot.
  if (googleEnabled && !webClientId) {
    devError(
      '[SocialLogin] VITE_SSO_GOOGLE_ENABLED=true but VITE_GOOGLE_WEB_CLIENT_ID is not set ' +
        'for this tier. Native Google sign-in will fail; tapping the Google button errors at runtime. ' +
        'Set it in .env.android.<tier>.',
    );
  }

  // iOS additionally needs the tier's iOS OAuth client id (GoogleSignIn SDK;
  // the ID token's `aud` is this client id, so the operator must also add it
  // to GOOGLE_ALLOWED_AUDIENCES on the API). Without it the plugin is
  // guaranteed to fail at login on iOS, so we skip the provider entirely and
  // leave the composable's uninitialized fallback in charge.
  const iOSClientId =
    googleEnabled && platform.isIOS() ? import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID : undefined;
  const googleReady = platform.isIOS() ? !!(webClientId && iOSClientId) : !!webClientId;

  if (googleEnabled && platform.isIOS() && webClientId && !iOSClientId) {
    devError(
      '[SocialLogin] VITE_SSO_GOOGLE_ENABLED=true but VITE_GOOGLE_IOS_CLIENT_ID is not set ' +
        'for this tier — native Google sign-in is disabled on iOS. Register an iOS OAuth ' +
        'client for this bundle id, set it in .env.ios.<tier>, and add it to ' +
        'GOOGLE_ALLOWED_AUDIENCES on the API.',
    );
  }

  // Plugin config supports any combination of providers — only ones we
  // actually have credentials for get configured.
  const initConfig: Parameters<typeof SocialLogin.initialize>[0] = {};

  if (webClientId && googleReady) {
    initConfig.google = {
      webClientId,
      ...(iOSClientId ? { iOSClientId } : {}),
      // 'online' returns idToken + accessToken without a refresh token.
      // 'offline' returns a serverAuthCode (single-use) the server can
      // exchange for a refresh token. We use 'online' because our API
      // already mints its own Passport refresh tokens — Google's refresh
      // token would be redundant.
      mode: 'online',
    };
  }

  if (appleClientId) {
    initConfig.apple = {
      clientId: appleClientId,
      // redirectUrl: empty string on iOS (per the plugin's own guidance —
      // 'Use empty string for iOS to prevent redirect'); for the
      // Android-WebView fallback we point at the backend's apple callback
      // so the WebView has somewhere to land. Scopes (email, name) live
      // on each login() call, not at init time.
      redirectUrl: platform.isIOS()
        ? ''
        : import.meta.env.VITE_APPLE_REDIRECT_URL || `${import.meta.env.VITE_APP_URL || ''}/auth/apple/callback`,
    };
  } else if (appleEnabled) {
    devLog(
      '[SocialLogin] VITE_SSO_APPLE_ENABLED=true but VITE_APPLE_CLIENT_ID is not set — ' +
        'native Apple sign-in disabled. Apple still works via redirect path on web/iOS.',
    );
  }

  // Nothing left to configure (e.g. iOS with google enabled but no
  // iOSClientId, and no apple credentials) — skip the plugin call entirely.
  if (!initConfig.google && !initConfig.apple) {
    devLog('[SocialLogin] No fully-configured native provider for this platform/tier.');
    return;
  }

  try {
    await SocialLogin.initialize(initConfig);
    devLog(
      `[SocialLogin] initialized (google=${!!initConfig.google}, apple=${!!initConfig.apple})`,
    );
  } catch (err) {
    devError('[SocialLogin] init failed:', err);
  }
});
